import { createContext, use } from 'react';

import { User } from '@auth/types/auth';

/**
 * Authentication context type definition that provides user state and auth operations
 */
export interface AuthContextType {
  /** Current authenticated user or null if not authenticated */
  user: User | null;
  /** Function to authenticate user with the token */
  login: (token: string) => Promise<void>;
  /** Function to log out the current user */
  logout: () => Promise<void>;
  /** Flag indicating if auth operations are in progress */
  isLoading: boolean;
}

/**
 * Context that provides authentication state and operations throughout the application
 * Initial values are provided for type safety but should be overridden by AuthProvider
 * @interface AuthContextType
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Hook to access authentication context
 * @throws {Error} If used outside of AuthProvider
 * @example
 * const { user, login, logout, isLoading } = useAuth();
 */
export const useAuth = () => {
  const context = use(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
